"use client";

import { useCallback, useEffect, useRef, type MouseEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion } from "motion/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Play } from "lucide-react";
import { aboutData } from "@/data/about";
import { useLenis } from "@/components/providers/LenisProvider";
import { AboutFeatureCard } from "./AboutFeatureCard";
import { cn } from "@/lib/utils";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const imageMotionRef = useRef<HTMLDivElement>(null);
  const lenis = useLenis();

  useEffect(() => {
    const section = sectionRef.current;
    const image = imageMotionRef.current;

    if (!section || !image) return;

    const context = gsap.context(() => {
      const reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;

      const revealItems = Array.from(
        section.querySelectorAll<HTMLElement>("[data-about-reveal]")
      );

      if (reducedMotion) {
        gsap.set(revealItems, { autoAlpha: 1, y: 0 });
        gsap.set(image, { yPercent: 0, scale: 1.02 });
        return;
      }

      gsap.from(revealItems, {
        autoAlpha: 0,
        y: 30,
        duration: 0.8,
        stagger: 0.06,
        ease: "power3.out",
        scrollTrigger: {
          trigger: section,
          start: "top 80%",
          once: true,
        },
      });

      // Smooth up/down floating movement matching the design specs
      gsap.set(image, { yPercent: -3, scale: 1.06 });
      gsap.to(image, {
        yPercent: 3,
        duration: 5,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
      });
    }, section);

    return () => context.revert();
  }, []);

  const smoothAnchor = useCallback(
    (event: MouseEvent<HTMLAnchorElement>, selector: string) => {
      const target = document.querySelector<HTMLElement>(selector);
      if (!target || !lenis) return;
      event.preventDefault();
      lenis.scrollTo(target, { offset: -32, duration: 1.15 });
    },
    [lenis]
  );

  return (
    <section
      ref={sectionRef}
      id="about"
      className="relative isolate w-full bg-[#f4f3f7] px-3 py-6 md:px-6 md:py-8"
    >
      <div className="mx-auto w-full max-w-[1400px] rounded-[36px] bg-white px-8 py-10 md:px-12 md:py-14 shadow-[0_10px_40px_rgba(0,0,0,0.02)]">
        
        {/* Top bar controls */}
        <div data-about-reveal className="flex items-center justify-between w-full mb-10">
          <span className="rounded-full border border-black/15 px-6 py-2 text-[13px] text-black/70 font-normal">
            {aboutData.category}
          </span>

          <div className="flex flex-col items-end gap-1.5">
            <span className="rounded-full border border-black/15 px-7 py-1.5 text-[13px] text-black/70 font-normal bg-white">
              {aboutData.catalog}
            </span>

            {/* <div className="flex items-center gap-2.5 rounded-full bg-[#111216] py-1 pl-5 pr-1 text-[12px] text-white font-medium">
              <span>Talk to Artisan: Arjun Singh</span>
              <div className="relative h-7 w-7 shrink-0 overflow-hidden rounded-full">
                <Image
                  src={aboutData.expert.image}
                  alt="Arjun Singh"
                  fill
                  className="object-cover"
                />
              </div>
            </div> */}
          </div>
        </div>

        {/* Main section row layout */}
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:items-start lg:gap-14">
          
          {/* Left Hero Text */}
          <div className="flex flex-col items-start pt-2">
            <h2
              data-about-reveal
              className="text-[clamp(2.5rem,5.5vw,4.8rem)] font-normal leading-[1.02] tracking-tight text-[#111216]"
            >
              {aboutData.title.map((line, idx) => (
                <span
                  key={idx}
                  className={cn(
                    "block",
                    line.emphasis &&
                      "font-bold italic underline decoration-[3px] underline-offset-[6px]"
                  )}
                >
                  {line.text}
                </span>
              ))}
            </h2>

            <p
              data-about-reveal
              className="mt-8 max-w-[440px] text-[14px] leading-relaxed text-black/50"
            >
              {aboutData.description}
            </p>

            <div data-about-reveal className="mt-8 flex items-center gap-4">
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Link
                  href="/about-us"
                  className="inline-block rounded-full bg-[#ee897a] px-8 py-3.5 text-[13px] font-semibold text-white shadow-sm"
                >
                  Explore Now
                </Link>
              </motion.div>

              <motion.button
                type="button"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="flex h-12 w-12 items-center justify-center rounded-full bg-[#111216] text-white"
              >
                <Play className="h-4 w-4 fill-white ml-0.5" />
              </motion.button>
            </div>
          </div>

          {/* Right Hero Image Frame */}
          <div
            data-about-reveal
            className="relative w-full aspect-[1.28] overflow-hidden rounded-[40px] bg-[#dedbd4]"
          >
            <div ref={imageMotionRef} className="absolute -inset-[5%] will-change-transform">
              {/* <Image
                src={aboutData.image.src}
                alt={aboutData.image.alt}
                fill
                priority
                className="object-cover"
              /> */}

              <video
                  className="object-cover w-full h-full"
                  autoPlay
                  muted
                  loop
                  playsInline
              >
                  <source src={aboutData.video.src} type="video/mp4" />
                  Your browser does not support the video tag.
             </video>
            </div>
          </div>
        </div>

        {/* Bottom Content Row */}
        <div className="mt-14 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-end lg:gap-14">
          
          {/* Cards */}
          <div className="grid gap-5 sm:grid-cols-2">
            {aboutData.featureCards.map((card) => (
              <AboutFeatureCard key={card.title} card={card} />
            ))}
          </div>

          {/* Stats matrix and Footer Nav */}
          <div data-about-reveal className="flex h-full flex-col justify-between w-full">

            {/* Stats — larger, editorial scale */}
            <div className="grid grid-cols-3 gap-6 pb-8 border-b border-black/10">
              {aboutData.stats.map((stat) => (
                <div key={stat.label} className="flex flex-col gap-1.5">
                  <p className="text-[clamp(2rem,3.2vw,3rem)] font-normal leading-none tracking-tight text-[#111216]">
                    {stat.value}
                  </p>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-black/40">
                    {stat.label}
                  </p>
                </div>
              ))}
            </div>

            {/* Highlight callout — fills the middle instead of leaving dead space */}
            <div className="flex items-start gap-4 py-8">
              <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#ee897a]" />
              <p className="text-[17px] sm:text-[19px] font-normal leading-snug text-[#111216] max-w-[420px]">
                {aboutData.highlight.text}
              </p>
            </div>

            {/* Link Bar Pill */}
            <div className="flex flex-wrap items-center justify-between rounded-full border border-black/10 px-8 py-4 bg-white">
              {aboutData.footerLinks.map((link, index) => {
                const targetId =
                  index === aboutData.footerLinks.length - 1 ? "#contact" : "#about";
                return (
                  <a
                    key={link}
                    href={targetId}
                    onClick={(e) => smoothAnchor(e, targetId)}
                    className="text-[13px] font-bold text-[#111216] transition-colors hover:text-[#ee897a]"
                  >
                    {link}
                  </a>
                );
              })}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}